import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Trash2, ThumbsUp, ThumbsDown, Eye, AlertTriangle } from "@/components/icons";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/errors";
import { ErrorState } from "@/components/ListState";
import { DeleteConfirm } from "@/components/DeleteConfirm";
import { KnownSolutions, toPublicArticle } from "@/components/KnownSolutions";
import { Page } from "@/components/PageConsole";
import { MediaGallery } from "@/components/MediaGallery";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

// Read view for one knowledge article: what the owner wrote, and one tap away,
// exactly what the wash-street employee sees after scanning a sticker.
export default function KbArticleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [article, setArticle] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [a, c] = await Promise.all([
        api.get(`/kb-articles/${id}`),
        api.get("/asset-categories"),
      ]);
      setArticle(a.data);
      setCategories(c.data);
    } catch (e) {
      setError(getErrorMessage(e, "Kan artikel niet laden."));
    } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const catName = (cid) => categories.find((c) => c.category_id === cid)?.name || cid;

  const remove = async () => {
    try {
      await api.delete(`/kb-articles/${id}`);
      toast.success("Artikel verwijderd");
      navigate("/kennisbank");
    } catch (e) { toast.error(getErrorMessage(e, "Verwijderen mislukt.")); }
  };

  if (loading) return <div className="page-body space-y-3"><Skeleton className="h-8 w-64" /><Skeleton className="h-40 w-full" /></div>;
  if (error) return <div className="page-body"><ErrorState testid="kb-detail-error" message={error} onRetry={load} /></div>;
  if (!article) return null;

  const publicArticle = toPublicArticle(article);

  const consoleFacts = [
    catName(article.category_id),
    ...(article.is_draft ? ["concept"] : []),
    article.visible_on_qr ? "zichtbaar op QR" : "verborgen op QR",
  ];

  const consoleActions = [
    { key: "preview", label: "Zo ziet de melder het", icon: Eye, variant: "primary",
      testid: "kb-detail-preview-button", onClick: () => setPreview(true) },
    ...(canWrite()
      ? [{ key: "bewerken", label: "Bewerken", icon: Pencil,
           testid: "kb-detail-edit", onClick: () => navigate(`/kennisbank?edit=${article.id}`) }]
      : []),
  ];

  return (
    <Page
      console={{
        variant: "view",
        title: article.title,
        facts: consoleFacts,
        actions: consoleActions,
        srTestid: "kb-detail-title",
        backSlot: (
          <button
            type="button"
            aria-label="Terug naar de kennisbank"
            data-testid="kb-detail-back"
            onClick={() => navigate("/kennisbank")}
            className="-ml-2 flex h-10 w-10 shrink-0 items-center justify-center rounded text-white hover:bg-console-diep"
          >
            <ArrowLeft size={18} />
          </button>
        ),
      }}
    >
      <div className="space-y-4">
      <span className="sr-only" data-testid="kb-detail-category">{catName(article.category_id)}</span>

      <Card className="space-y-4 p-5">
        {article.symptoms?.length > 0 && (
          <div>
            <h2 className="mb-2 text-body font-semibold text-foreground">Klachten / symptomen</h2>
            <ul className="space-y-1" data-testid="kb-detail-symptoms">
              {article.symptoms.map((s, i) => (
                <li key={i} className="flex gap-2 text-body text-foreground">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 bg-plaat" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <h2 className="mb-2 text-body font-semibold text-foreground">Oplossing, stap voor stap</h2>
          {article.solution_steps?.length ? (
            <ol className="space-y-2" data-testid="kb-detail-steps">
              {article.solution_steps.map((s, i) => (
                <li key={i} className="flex gap-2 text-body text-foreground">
                  <span className="w-5 shrink-0 text-right font-medium tabular-nums text-muted-foreground">{i + 1}.</span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
          ) : (
            <p className="flex items-center gap-1.5 text-body text-muted-foreground" data-testid="kb-detail-nosteps">
              <AlertTriangle size={16} /> Nog geen stappen beschreven — dan ziet de melder ook niets bruikbaars.
            </p>
          )}
        </div>

        {article.media?.length > 0 && (
          <div>
            <h2 className="mb-2 text-body font-semibold text-foreground">Foto's en video</h2>
            <div data-testid="kb-detail-media"><MediaGallery media={article.media} /></div>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3 text-body text-muted-foreground" data-testid="kb-detail-feedback">
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center gap-1.5"><ThumbsUp size={16} /> {article.helpful_count || 0} keer geholpen</span>
            <span className="inline-flex items-center gap-1.5"><ThumbsDown size={16} /> {article.not_helpful_count || 0} keer niet</span>
          </div>
          {canWrite() && (
            <DeleteConfirm
              trigger={<Button variant="ghost" size="sm" data-testid="kb-detail-delete"><Trash2 size={18} className="mr-2" /> Artikel verwijderen</Button>}
              title={`Artikel "${article.title}" verwijderen?`}
              description="Dit kan niet ongedaan gemaakt worden."
              onConfirm={remove}
              testid="kb-detail-delete-confirm"
            />
          )}
        </div>
      </Card>

      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Zo ziet de melder het</DialogTitle>
            <DialogDescription>
              Dit is het echte publieke onderdeel van /m/&#123;qr_id&#125;, met alleen de velden die
              openbaar zijn. Feedback wordt hier niet opgeslagen.
            </DialogDescription>
          </DialogHeader>
          {!article.visible_on_qr && (
            <p className="rounded-sm border border-letop-rand bg-letop-vlak p-2 text-data text-letop-tekst" data-testid="kb-preview-hidden-warning">
              Dit artikel staat op verborgen, dus de melder ziet het nu niet. Hieronder staat hoe het
              eruit zou zien als je het zichtbaar maakt.
            </p>
          )}
          {/* The real public component, not a lookalike. */}
          <div className="bg-muted/30 p-3">
            <KnownSolutions
              testid="kb-preview-known-solutions"
              solutions={[publicArticle]}
              defaultOpenId={publicArticle.id}
              onHelpful={() => toast.info("Preview: feedback wordt niet opgeslagen.")}
              onNotHelpful={() => toast.info("Preview: feedback wordt niet opgeslagen.")}
            />
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </Page>
  );
}
